import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Input,
  Text,
  Badge,
  CircularProgress,
  CircularProgressLabel,
  Spinner,
  Tooltip
} from '@chakra-ui/react';
import { FaCloudUploadAlt, FaVideo, FaImage, FaFileAlt, FaTrashAlt } from 'react-icons/fa';
import { CheckIcon } from '@chakra-ui/icons'
import { ResourcesPage } from '../../pages/ResourcesPage';
import { ModalBox } from "../ModalBox";
import { useResourceFileUpload } from "./useResourceFileUpload"
import { useStore } from "../../Store";

export const ResourceFileUpload = (
  {
    selectedFilesFromGallery, 
    selectedNewFiles,
    extractTextFromAllFiles,
    createProductInDB,
    type,
  }) => {
  const {
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    toggleIncludeInOutline,
    removeNewFile,
    removeGalleryFile,
    isDragging,
    fileInputRef,
    openResourceModal,
    isOpen,
    fileType,
    selectMedia,
    onCloseResourceModal,
    onSelectFiles,
    uploadNewFiles,
    uploadedFilesRef,
    setIsGenerateOutline
  } = useResourceFileUpload({
    type, 
    selectedFilesFromGallery, 
    selectedNewFiles, 
  });
  const {bookUploadProgress, courseUploadProgress, bookIsUploading, courseIsUploading} = useStore((state) => state);
  let isUploading = type === "book" ? bookIsUploading : courseIsUploading
  const isAllCompleted = type === "book" ? bookUploadProgress?.every(progress => progress?.status === "completed") : courseUploadProgress?.every(progress => progress?.status === "completed")
  const isUploadDisabled = 
  !selectedFilesFromGallery?.some(file => file.includeInOutline === true) &&
  !selectedNewFiles?.some(file => file.includeInOutline === true)
  let uploadProgress = type === "book" ? bookUploadProgress : courseUploadProgress
  return (
    <Box p={8}>
      <Box maxW="4xl" mx="auto">
        <Text fontSize="2xl" fontWeight="semibold" mb={1} textTransform="Capitalize">{type} Materials</Text>
        <Text color="gray.600" mb={6}>Upload new or choose existing videos, images, and documents for your course.</Text>
        <Text fontSize="xl" mb={2}>Upload New</Text>
        <Box
          borderWidth={2}
          borderStyle="dashed"
          borderRadius="lg"
          p={8}
          textAlign="center"
          borderColor={isUploading ? "gray.300" : isDragging ? "blue.500" : "gray.300"}
          bg={isUploading ? "gray.100" : isDragging ? "blue.50" : "white"}
          onDragEnter={!isUploading ? handleDragEnter : undefined}
          onDragOver={!isUploading ? handleDragEnter : undefined}
          onDragLeave={!isUploading ? handleDragLeave : undefined}
          onDrop={!isUploading ? handleDrop : undefined}
          position="relative"
          pointerEvents={isUploading ? "none" : "auto"}
          cursor={isUploading ? "not-allowed" : "pointer"}
          opacity={isUploading ? 0.6 : 1}
        >
          <Icon as={FaCloudUploadAlt} boxSize={10} color="gray.400" mb={4} />
          <Text color="gray.600" mb={2}>Drag and drop files here</Text>
          <Text color="gray.500" mb={4}>or</Text>
          <Button colorScheme="blue">Browse Files</Button>
          <Input
            type="file"
            ref={fileInputRef}
            position="absolute"
            top={0}
            left={0}
            opacity={0}
            width="100%"
            height="100%"
            cursor="pointer"
            onChange={handleFileInputChange}
            multiple
          />
          <Text color="gray.500" mt={4}>Maximum file size: 500MB</Text>
        </Box>

        <Text fontSize="xl" mt={8} mb={2}>Select From Gallery</Text>
        <Flex justify="space-between" wrap="wrap" gap={4}>
          {[{ type: 'videos', icon: FaVideo, label: 'Video', types: 'MP4, MOV, AVI' }, { type: 'images', icon: FaImage, label: 'Images', types: 'JPG, PNG, GIF' }, { type: 'pdfs', icon: FaFileAlt, label: 'Documents', types: 'PDF' }].map((item, index) => (
            <Box
              key={index}
              textAlign="center"
              p={4}
              bg="white"
              borderRadius="lg"
              boxShadow="sm"
              flex="1"
              onClick={() => {
                if (!isUploading) openResourceModal(item.type);
              }}
              cursor={isUploading ? "not-allowed" : "pointer"}
              pointerEvents={isUploading ? "none" : "auto"}
              transition="all 0.2s ease-in-out"
              _hover={
                !isUploading
                  ? { transform: "scale(1.05)", boxShadow: "md", bg: "blue.100" }
                  : {}
              }
              opacity={isUploading ? 0.5 : 1}
            >
              <Icon as={item.icon} boxSize={6} color="blue.500" mb={2} />
              <Text fontWeight="medium">{item.label}</Text>
              <Text fontSize="sm" color="gray.500">{item.types}</Text>
            </Box>
          ))}
        </Flex>
        {
          selectedFilesFromGallery.length > 0 || selectedNewFiles.length > 0 ? 
          <Box bg="white" borderRadius="lg" boxShadow="sm" mt={10} p={6}>
            {selectedFilesFromGallery.length > 0 && (
              <>
                <Text fontSize="lg" fontWeight="medium" mb={2}>Gallery</Text>
                {selectedFilesFromGallery.map((file, index) => (
                  <Flex key={file.key} justify="space-between" align="center" py={3} borderBottomWidth={1}>
                    <Flex align="center">
                      <Icon as={file.type === 'videos' ? FaVideo : file.type === 'images' ? FaImage : FaFileAlt} color="blue.500" mr={3} />
                      <Box>
                        <Flex justifyContent="flex-start" alignItems="center">
                          <Text fontWeight="medium" mr={2}>{file.name}</Text>
                        </Flex>
                      </Box>
                    </Flex>
                    {
                      isUploading ? <CheckIcon color="green.400" /> :
                        <Flex align="center">
                          {/* <Checkbox isChecked={file.includeInOutline} onChange={() => toggleIncludeInOutline(file)} mr={4}>Include in course outline</Checkbox> */}
                          <Icon fontSize="md" as={FaTrashAlt} color="red.500" cursor="pointer" onClick={() => removeGalleryFile(index)} />
                        </Flex>
                    }
                  </Flex>
                ))}
              </>
            )}
            {selectedNewFiles.length > 0 && (
              <>
                <Text fontSize="lg" fontWeight="medium" mt={4} mb={2}>New Files</Text>
                {selectedNewFiles.map((file, index) => (
                  file.file instanceof File || uploadProgress[index]?.status === "completed" ?
                  <Flex key={file.key} justify="space-between" align="center" py={3} borderBottomWidth={1}>
                    <Flex align="center">
                      <Icon as={file.type === 'videos' ? FaVideo : file.type === 'images' ? FaImage : FaFileAlt} color="blue.500" mr={3} />
                      <Box>
                        <Flex justifyContent="flex-start" alignItems="center">
                          <Text fontWeight="medium" mr={2} color={file.invalid ? "red" : "black.400"}>{file.name}</Text>
                          {file.isNew ? <Badge colorScheme='green'>New</Badge> : <></>}
                        </Flex>
                        {/* { Might need this in next iteration
                        file.type === 'images' ? 
                        <Text fontSize="sm" color="gray.500">{fileSize(file)}</Text> :
                        <Text fontSize="sm" color="gray.500">{file.size}</Text>
                      } */}
                      </Box>
                    </Flex>
                    {
                      isUploading ? file.isNew ? uploadProgress[index]?.progress !== 100 ?
                        (
                          <CircularProgress value={uploadProgress[index]?.progress || 0} color="green.400" size="40px">
                            <CircularProgressLabel>{uploadProgress[index]?.progress || 0}%</CircularProgressLabel>
                          </CircularProgress>
                        ) : uploadProgress[index]?.status === "completed" ? <CheckIcon color="green.400" /> : uploadProgress[index]?.status === "processing" ? <Flex alignItems="center" justifyContent="flex-start"><Spinner size='xs' color="orange.400" /><Text color="orange.400" ml={1}>processing</Text></Flex> : null : <CheckIcon color="green.400" /> : null
                    }
                    {
                      isUploading || uploadProgress[index]?.progress === 100 ? <></> :
                        <Flex align="center">
                          {/* <Checkbox isChecked={file.includeInOutline} onChange={() => toggleIncludeInOutline(file)} mr={4}>Include in course outline</Checkbox> */}
                          <Icon fontSize="md" as={FaTrashAlt} color="red.500" cursor="pointer" onClick={() => removeNewFile(index)} />
                        </Flex>
                    }
                  </Flex> : <></>
                ))}
              </>
            )}
          </Box> : <></>
        }
        <Flex justifyContent="flex-end" mt={8}>
          {!isUploading ? <Button mr={3} onClick={createProductInDB}>Skip</Button> : null}
          {
            selectedFilesFromGallery.length > 0 && selectedNewFiles.length === 0 ?
            <Button
              colorScheme="blue"
              variant='solid'
              spinnerPlacement='start'
              onClick={() => {
                setIsGenerateOutline(true)
                extractTextFromAllFiles(null, selectedFilesFromGallery)
              }}
              disabled={!selectedFilesFromGallery?.some(file => file.includeInOutline === true)}
            >
              Generate Outline
            </Button> : 
            !isAllCompleted || Object.keys(uploadProgress).length === 0 ?
            <Tooltip
              label={
                isUploadDisabled
                  ? 'Select at least one file checkbox to include in outline'
                  : 'Click to upload selected files'
              }
              hasArrow
              bg="gray.700"
            >
              <Button
                isLoading={isUploading}
                loadingText='Uploading'
                colorScheme="blue"
                variant='solid'
                spinnerPlacement='start'
                onClick={uploadNewFiles}
                disabled={isUploadDisabled}
              >
                Upload
              </Button>
            </Tooltip>
             :
              <Box> 
                <Flex justifyContent="flex-end">
                  <Button
                    colorScheme="blue"
                    variant='solid'
                    spinnerPlacement='start'
                    onClick={() => {
                      setIsGenerateOutline(true)
                      const files = uploadedFilesRef.current.length > 0 ? uploadedFilesRef.current : selectedNewFiles
                      extractTextFromAllFiles(files, [...files, ...selectedFilesFromGallery])
                    }}
                  >
                    Generate Outline
                  </Button>
                </Flex>
              </Box>
          }
        </Flex>
      </Box>
      <ModalBox
        title="Select"
        isOpen={isOpen}
        onClose={onCloseResourceModal}
        isFooter={true}
        onCancel={onCloseResourceModal}
        confirmButtonType={true}
        onConfirm={onSelectFiles}
        confirmButtonText="Select"
        size="full"
        isHideDivider={true}
        description={
          <ResourcesPage type={fileType} isHideFilter={true} isSelection={true} isHideUpload={true} imageUrl={selectedFilesFromGallery.map(f => f.url)} videoUrl={selectedFilesFromGallery.map(f => f.url)} pdfUrl={selectedFilesFromGallery.map(f => f.url)} selectMedia={selectMedia} />
        }
      ></ModalBox>
    </Box>
  );
};
